import React from 'react'
import InfoPill, { InfoPillProps } from '../infoPill'
import { AutomationTarget } from '../../../types'
import Icon from '../icon'
import Animate from '../animate'
import { ColorNames } from '../../../constants/colorVars'
import { getCssColorFromColorName } from '../../../utils/colors'

const FADE_TIMEOUT_MS = 300

export enum ChipColor {
  Info = ColorNames.Info,
  Success = ColorNames.Success,
  Caution = ColorNames.Caution,
  Error = ColorNames.Error,
  Purple = ColorNames.Purple,
  Gray = ColorNames.Gray,
  GrayDark = ColorNames.GrayDark,
}

export interface ChipProps extends AutomationTarget {
  content: InfoPillProps['content']
  size?: InfoPillProps['size']
  type?: 'filter' | 'input' | 'button'
  isSelected?: boolean // only for filter and button type.  Input type always selected.
  selectionStyle: 'dark' | 'light'
  borderless?: boolean
  disabled?: boolean
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  color?: ChipColor
  className?: string
  style?: React.CSSProperties
  /** Icon name to show when filter is unselected. Selected state always shows check. */
  iconName?: string
  /** Font weight for the chip label text */
  fontWeight?: 'normal' | 'bold'
}

const colorToExtraLight: Record<ChipColor, ColorNames> = {
  [ChipColor.Info]: ColorNames.InfoExtraLight,
  [ChipColor.Success]: ColorNames.SuccessExtraLight,
  [ChipColor.Caution]: ColorNames.CautionExtraLight,
  [ChipColor.Error]: ColorNames.ErrorExtraLight,
  [ChipColor.Purple]: ColorNames.PurpleExtraLight,
  [ChipColor.Gray]: ColorNames.White,
  [ChipColor.GrayDark]: ColorNames.White,
}

const colorToMedium: Record<ChipColor, ColorNames> = {
  [ChipColor.Info]: ColorNames.InfoMedium,
  [ChipColor.Success]: ColorNames.SuccessMedium,
  [ChipColor.Caution]: ColorNames.CautionMedium,
  [ChipColor.Error]: ColorNames.ErrorMedium,
  [ChipColor.Purple]: ColorNames.PurpleMedium,
  [ChipColor.Gray]: ColorNames.GrayMediumLight,
  [ChipColor.GrayDark]: ColorNames.GrayMediumLight,
}
/**
 * Chip Component
 * A component that displays a small block of information, often used for tags or filters.
 * If type=filter, it can be selected or unselected.
 * If type=input, it is always selected and acts as an part input field (search -> select)
 * If type=button, it renders with no icon, no border, and no state toggle.
 *   Uses dark style when isSelected=true, light/unselected style otherwise.
 *   Useful as a clickable label/link that uses the Chip color system.
 */
const Chip: React.FC<ChipProps> = ({
  content,
  size = 'medium',
  type = 'filter',
  selectionStyle: _selectionStyle,
  isSelected: _isSelected = false,
  borderless = false,
  disabled = false,
  onClick,
  color,
  className,
  style,
  elmId = 'chip',
  iconName,
  fontWeight = 'bold',
}) => {
  /**
   * Default color is a special case used forms and filters that has the new look of gray when unselected and
   * ExtraLight info when selected.
   */
  const useDefaultColors = !color
  const [isVisible, setIsVisible] = React.useState(true) // used for input type fade
  const isSelected = _isSelected || type === 'input'
  const selectionStyle =
    _selectionStyle || (type === 'input' ? 'light' : 'dark')
  const includeBorder =
    !borderless &&
    (selectionStyle === 'light' || !isSelected || type === 'button')

  let backgroundColor
  let foregroundColor

  if (!isSelected) {
    // unselected, simple light background, dark foreground
    backgroundColor =
      colorToExtraLight[useDefaultColors ? ChipColor.Gray : color]
    foregroundColor = useDefaultColors ? ColorNames.Gray : color

    // button type is borderless, so use OffWhiteDark instead of White for visibility
    if (type === 'button' && backgroundColor === ColorNames.White) {
      backgroundColor = ColorNames.OffWhiteDark
    }
  } else {
    // selected, depends on selection style
    const selectionColor = useDefaultColors ? ChipColor.Info : color
    if (selectionStyle === 'dark') {
      backgroundColor = selectionColor
      foregroundColor = ColorNames.White
    } else {
      backgroundColor = colorToExtraLight[selectionColor]
      foregroundColor = selectionColor
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled && onClick) {
      if (type === 'filter' || type === 'button')
        // toggling selection (filter) or simple click (button)
        onClick(e)
      else if (type === 'input') {
        // closing ...
        setIsVisible(false)
        setTimeout(() => {
          onClick(e)
        }, FADE_TIMEOUT_MS)
      }
    }
  }

  const getBorderStyle = (): React.CSSProperties | undefined => {
    if (borderless) {
      return undefined
    }
    if (!isSelected && useDefaultColors) {
      return {
        borderColor: getCssColorFromColorName(ColorNames.GrayMediumLight),
      }
    }
    if (!isSelected && !useDefaultColors) {
      return { borderColor: getCssColorFromColorName(colorToMedium[color]) }
    }
    if (isSelected && selectionStyle === 'light') {
      const chipColorKey = useDefaultColors ? ChipColor.Info : color
      return {
        borderColor: getCssColorFromColorName(colorToMedium[chipColorKey]),
      }
    }
    return undefined
  }

  const pill = (
    <InfoPill
      content={content}
      size={size}
      backgroundColor={backgroundColor as ColorNames}
      foregroundColor={foregroundColor as unknown as ColorNames}
      includeBorder={includeBorder}
      interactive={type === 'input' ? 'icon' : 'button'}
      disabled={disabled}
      iconOnRight={type !== 'filter' && type !== 'button'}
      fontWeight={fontWeight}
      style={getBorderStyle()}
      icon={
        // eslint-disable-next-line no-nested-ternary
        isSelected && type !== 'button' ? (
          <Icon
            elmId={`${elmId}-${type === 'filter' ? 'check' : 'close'}`}
            fitted
            name={type === 'filter' ? 'check' : 'xmark'}
            iconStyle='solid'
            color={foregroundColor as unknown as ColorNames}
            onClick={type === 'input' ? handleClick : undefined}
          />
        ) : !isSelected && type === 'filter' && iconName ? (
          <Icon
            elmId={`${elmId}-icon`}
            fitted
            name={iconName}
            iconStyle='solid'
            color={
              useDefaultColors
                ? ColorNames.Gray
                : (foregroundColor as unknown as ColorNames)
            }
            style={{ fontSize: '1em' }}
          />
        ) : undefined
      }
    />
  )

  return (
    <div
      className={className}
      style={style}
      data-elm-id={elmId}
      onClick={type === 'filter' || type === 'button' ? handleClick : undefined}
      tabIndex={disabled ? -1 : 0}
      role='button'
      aria-pressed={type === 'button' ? undefined : isSelected}
      aria-disabled={disabled}
    >
      {/*
        FadeInOut (react-spring) is only meaningful for `input` chips, which fade
        out on close via setIsVisible(false). For `filter`/`button` isVisible is
        always true, so the wrapper only parks an animated value on react-spring's
        frameloop — which wedges into an infinite rAF loop when the chip is toggled
        through a `display:none` ancestor (e.g. a responsive breakpoint). Render
        the pill directly for those types.
      */}
      {type === 'input' ? (
        <Animate.FadeInOut isVisible={isVisible} duration={FADE_TIMEOUT_MS}>
          {pill}
        </Animate.FadeInOut>
      ) : (
        pill
      )}
    </div>
  )
}

export default Chip
